import * as React from 'react';
import { cn } from '../lib/cn.ts';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      data-invalid={invalid || undefined}
      className={cn(
        'h-10 w-full rounded-md bg-bg-raised px-3 text-sm text-fg shadow-flat',
        'placeholder:text-fg-subtle',
        'transition-[box-shadow,background-color] duration-quick ease-out-expo',
        'focus:outline-none focus-visible:shadow-[var(--elevation-flat),0_0_0_2px_rgb(var(--accent-default)_/_0.45)]',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        'data-[invalid]:shadow-[var(--elevation-flat),0_0_0_1px_rgb(var(--danger)_/_0.7)]',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
};

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      data-invalid={invalid || undefined}
      className={cn(
        'min-h-[120px] w-full rounded-md bg-bg-raised px-3 py-2 text-sm text-fg shadow-flat',
        'placeholder:text-fg-subtle',
        'transition-[box-shadow,background-color] duration-quick ease-out-expo',
        'focus:outline-none focus-visible:shadow-[var(--elevation-flat),0_0_0_2px_rgb(var(--accent-default)_/_0.45)]',
        'data-[invalid]:shadow-[var(--elevation-flat),0_0_0_1px_rgb(var(--danger)_/_0.7)]',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;
export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn('text-xs font-medium uppercase tracking-[0.18em] text-fg-subtle', className)}
      {...props}
    />
  ),
);
Label.displayName = 'Label';

export const FieldError = ({ children }: { children: React.ReactNode }) =>
  children ? (
    <p role="alert" aria-live="polite" className="mt-1 text-xs text-danger">
      {children}
    </p>
  ) : null;
